import {css, StyleSheet} from 'aphrodite';
import React, {ReactNode} from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import {assert} from 'Asserts';
import {StandardFeature} from 'auth/capabilities/CapabilityConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {Permissions} from 'auth/PermissionConstants';
import {isInaccessibleInfo} from 'auth/RequestAccess';
import BenchlingConfig from 'BenchlingConfig';
import requestFetchValidationExtras from 'bioentities/requestFetchValidationExtras';
import {PartJSON} from 'biojs/Part';
import {ModifiedNucleotides} from 'biojs/Sequence';
import {TranslationAttributes} from 'biojs/Translation';
import BufferOptions from 'buffers/BufferOptions';
import DataConnector from 'connect/DataConnector';
import {FolderWithPermissions, NumberIDFolder, StringIDFolder} from 'constants/FolderConstants';
import ModelTypeConstants, {SequenceSubtype} from 'constants/ModelTypeConstants';
import {FinderResult} from 'constants/NavConstants';
import {BioentityAliasInfo, TagSchema} from 'constants/RegistryConstants';
import {User} from 'constants/UserConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FinderActions, {TagUpdateOptions} from 'file-browser/FinderActions';
import {SchemaTag, Tag} from 'folder-items/FileConstants';
import * as BioentityActions from 'inventory/BioentityActions';
import {HoistedTag} from 'inventory/InventoryFileConstants';
import InventoryFileStore from 'inventory/InventoryFileStore';
import MetadataEditorInfoSection from 'metadata/MetadataEditorInfoSection';
import {REQUIRED_FIELD_ERROR_LABEL} from 'metadata/SchemaFieldValueError';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import AaSequenceStore from 'molbio/aa-sequences/AaSequenceStore';
import {GeneralNoteViewProps} from 'notebook/notes/tabs/NoteViewProps';
import * as ReviewUtil from 'notebook/reviews/ReviewUtil';
import ProcessMetadataSection from 'processes/process-details/ProcessMetadataSection';
import {getParentLinkFileId} from 'registry/child-entities/ChildEntityConstants';
import IngredientTable from 'registry/mixtures/ingredient-table/IngredientTable';
import {isMixtureEditable} from 'registry/mixtures/MixtureConstants';
import MixtureInstructions from 'registry/mixtures/MixtureInstructions';
import {getParentLinkMixtureTag} from 'registry/mixtures/ParentMixtureHelpers';
import * as RegistryValidatorActions from 'registry/RegistryValidatorActions';
import RegistryValidatorStore from 'registry/RegistryValidatorStore';
import ValidationStateFetcher from 'registry/validation/ValidationStateFetcher';
import {isIntegrationField} from 'schemas/fields/integration/IntegrationFieldUtil';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {isFailed} from 'schemas/SchemaValidationChecks';
import {
  SchemaValidationErrorRecord,
  SchemaValidationState,
  ValidationExtras,
} from 'schemas/SchemaValidationConstants';
import ComputedTagFetcher from 'schemas/tags/computed/ComputedTagFetcher';
import * as TagUtil from 'schemas/tags/TagUtil';
import EditableNotifications from 'studies/notifications/EditableNotifications';
import {QueryingStudyDescriptionEditor} from 'studies/workspace/StudyDescriptionEditor';
import StudySchemaTagTable from 'studies/workspace/StudySchemaTagTable';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import * as TagSchemaUtil from 'tag-schema/util/TagSchemaUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import licenses from 'user/Licenses';
import APIUtil from 'util/APIUtil';
import {Optional} from 'util/Types';

import AliasField from './AliasField';
import AliasList from './AliasList';
import ConvertedToItemField from './ConvertedToItemField';
import MetadataEditorToolbar from './MetadataEditorToolbar';
import MetadataErrors from './MetadataErrors';
import MetadataTagTable from './MetadataTagTable';
import SchemaTagTable from './SchemaTagTable';

export type MetadataEditorProps = Props & ConnectedProps;

interface Props {
  archiveRecord?: ArchiveRecord | null;
  authors?: Array<User> | null;
  bufferOptionsText: string | null;
  // Workflow entry templates use this component and do not have createdAt
  createdAt?: string;
  customInfoBanner?: ReactNode;
  sequenceSubtype?: SequenceSubtype;
  fileId: string;
  fileName: string;
  finalized?: {
    isFinalized: boolean;
    finalizedAt: Optional<string>;
  };
  // `folder` is optional for non-folder-items (i.e. templates)
  folder?: NumberIDFolder | StringIDFolder;
  /* User has access to custom tags (currently always true) and the item type supports custom tags */
  hasCustomTagSupport: boolean;
  /* User has access to file schemas and the item type supports it */
  hasFileSchemaSupport: boolean;
  /* User has access to registry and the item type supports it */
  hasRegistrySupport: boolean;
  humanId?: string;
  isRegistryWritable: boolean;
  instructionsEditorProps?: GeneralNoteViewProps;
  onAnalyzeAaSequences: (proteinIds: Array<string>) => void;
  permissions: Permissions;
  projectFolderId?: string | null;
  registryId?: string | null;
  registryName?: string;
  schemaValidationState?: SchemaValidationState | null;
  sequenceBases?: string | null;
  sequenceParts?: Array<PartJSON>;
  sequenceTranslations?: Array<TranslationAttributes>;
  sequenceCircular?: boolean | null;
  sequenceModifiedNucleotides?: ModifiedNucleotides | null;
  editURL?: string;
  shouldAllowChangeCollection: boolean;
  shouldAllowTeamAuthorsLookup?: boolean;
  tags: Array<Tag>;
  /* TagSchema of item even if user does not have access. Gate access via hasFileSchemaSupport */
  tagSchema: TagSchema | null;
  updateName: (name: string) => Promise<string | void>;

  // Whether or not the object is a non-interactive Process (aka TaskGroup)
  // Non-interactive processes cannot be edited or archived. The only edit action available is to move the process
  // to a different folder as an escape hatch to handle permissions issues.
  isNonInteractiveProcess?: boolean;
}

interface ConnectedProps {
  bioentityAliasInfos: Array<BioentityAliasInfo>;
  hoistedTags: Array<HoistedTag>;
  isLoadingTags: boolean;
  projectFolder: NumberIDFolder | StringIDFolder | null;
  aaSequenceResidues?: string | null;
  reviewBlockedByErrors: boolean;
  tagErrors: Array<SchemaValidationErrorRecord>;
  validationExtras?: ValidationExtras;
}

interface State {
  orderedTags: Array<Tag>;
}

class MetadataEditorConnector extends DataConnector<Props, ConnectedProps> {
  override stores = [InventoryFileStore, RegistryValidatorStore, AaSequenceStore];

  override forceFetchOnMount(): void {
    // Fetch the file so we have the most current schema if it's a folder item and has a schema
    if (this.props.hasFileSchemaSupport && APIUtil.isFolderItem(this.props.fileId)) {
      FileBrowserActions.bulkFetchFileSummaries([this.props.fileId]);
    }
  }

  enhanceProps(): ConnectedProps {
    let projectFolder;
    if (this.props.projectFolderId == null) {
      projectFolder = null;
    } else {
      const folderOrInaccessibleInfo = this.getOrFetchMaybeInaccessibleRequired(
        ManagedModelType.FOLDER_BY_ID,
        this.props.projectFolderId
      );
      if (isInaccessibleInfo(folderOrInaccessibleInfo)) {
        projectFolder = null;
      } else {
        projectFolder = folderOrInaccessibleInfo;
      }
    }
    const validationExtras = this.reduxState().models.validationExtrasByBioentityId[this.props.fileId];
    const metadataTabState = this.reduxState().ui.metadataTab[this.props.fileId];
    const reviewBlockedByErrors = metadataTabState ? metadataTabState.reviewBlockedByErrors : false;
    return {
      projectFolder: projectFolder as FolderWithPermissions,
      validationExtras,
      reviewBlockedByErrors,
      bioentityAliasInfos: this.inventoryFileStore().getBioentityAliasInfos(this.props.fileId) || [],
      hoistedTags: this.inventoryFileStore().getHoistedTags(this.props.fileId) || [],
      isLoadingTags: this.inventoryFileStore().getIsLoadingTags(),
      aaSequenceResidues: APIUtil.isProtein(this.props.fileId)
        ? this.aaSequenceStore().getAaSequenceById(this.props.fileId)?.version?.aminoAcids
        : null,
      tagErrors: this.registryValidatorStore().getTagErrors(this.props.fileId) ?? [],
    };
  }
}

export default class MetadataEditor extends React.Component<Props & ConnectedProps> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any

  // Normally TypeScript can recognize defaultProps and will adjust the type of Props appropriately.
  // However, because MetadataEditor is only used via MetadataEditorConnector, these fields are not
  // recognized as defaultProps and are still considered to be potentially undefined.
  static defaultProps = {
    authors: null,
    sequenceBases: null,
    sequenceParts: [],
    sequenceTranslations: [],
    schemaValidationState: null,
  };

  override state: State = {
    orderedTags: this.props.tags, // orderedTags preserves ordering that tags are displayed to the user in
  };

  override componentDidMount(): void {
    if (this.props.hasRegistrySupport) {
      // Only fetch hoisted and computed tags for enterprise
      if (licenses.hasEnterprisePlan()) {
        BioentityActions.getHoistedTags(this.props.fileId);
      }
    }

    this.fetchValidation();
    if (this.props.hasRegistrySupport) {
      this.fetchSchemaValidationExtras();
    }
  }

  override componentDidUpdate(prevProps: Props): void {
    if (!prevProps.hasRegistrySupport && this.props.hasRegistrySupport) {
      this.fetchSchemaValidationExtras();
    }
  }

  override UNSAFE_componentWillReceiveProps(nextProps: Props): void {
    // Ensure all new tags come after existing tags so that user-facing tag table doesn't get reordered
    const [updatedTags, newTags] = Array.from(
      _.partition(nextProps.tags, (tag) => _.findWhere(this.state.orderedTags, {name: tag.name}) != null)
    );
    // Keep existing tag order
    const orderedUpdatedTags = [];
    for (const tag of Array.from(this.state.orderedTags)) {
      const updatedTag = _.findWhere(updatedTags, {name: tag.name});
      if (updatedTag != null) {
        orderedUpdatedTags.push(updatedTag);
      }
    }
    this.setState({orderedTags: orderedUpdatedTags.concat(newTags)});

    if (MetadataEditor.shouldFetchValidation(this.props, nextProps)) {
      this.fetchValidation(nextProps);
    }
  }

  private getSchemaRegistryId(props?: Props): Optional<string> {
    if (props == null) {
      ({props} = this);
    }
    return props.tagSchema?.registryId;
  }

  private static shouldFetchValidation(props: Props, nextProps: Props): boolean {
    // Refetch if registryId changes (e.g. file is unregistered), tag schema changes, or tags are updated
    if (props.registryId !== nextProps.registryId) {
      return true;
    }
    if (!isEqual(props.tagSchema, nextProps.tagSchema)) {
      return true;
    }
    return !isEqual(props.tags, nextProps.tags);
  }

  private shouldShowUnregisteredTagErrors(): boolean {
    return this.props.hasRegistrySupport;
  }

  private shouldShowReviewTagErrors = (): boolean =>
    ReviewUtil.canUserReviewType(APIUtil.apiIdToType(this.props.fileId)) &&
    (BenchlingConfig.ELN_ENABLE_SCHEMA_REQUIRED_FOR_REVIEW || this.props.tagSchema != null);

  private isRegistered = (): boolean => Boolean(this.props.registryId);

  private getPartialFeatureDefinition = (
    permissions?: Permissions
  ): {
    item: {id: string; permissions: Permissions | undefined};
    itemState: {isRegistered: boolean};
  } => ({
    item: {id: this.props.fileId, permissions},
    itemState: {isRegistered: this.isRegistered()},
  });

  private registeredValidationTagErrors = (): Array<SchemaValidationErrorRecord> | null =>
    this.props.schemaValidationState &&
    isFailed(this.props.schemaValidationState) &&
    this.props.validationExtras
      ? this.props.validationExtras.errorRecords
      : null;

  private getVisibleFieldErrors = (
    tagErrors: Array<SchemaValidationErrorRecord> | null
  ): Array<SchemaValidationErrorRecord> => {
    // Integration Field errors are usually hidden,
    // because the user shouldn't really do anything about them.
    // But if a user just tried to submit the entry for review,
    // and they got blocked, they need to see what errors they need to fix.
    // More details here: https://benchling.quip.com/kP1VAkOe85bC/Product-Spec-Integration-Fields
    let validationErrors = tagErrors || [];
    if (!this.props.reviewBlockedByErrors) {
      validationErrors =
        validationErrors &&
        validationErrors.filter(
          (err) =>
            !err.field || !(isIntegrationField(err.field) && err.errorLabel === REQUIRED_FIELD_ERROR_LABEL)
        );
    }
    return validationErrors;
  };

  private getFieldErrorsToDisplay = (): {[fieldId: number]: Array<SchemaValidationErrorRecord>} => {
    const tagErrors = this.registeredValidationTagErrors();
    const shouldShowErrors =
      (tagErrors && tagErrors.length) ||
      this.shouldShowUnregisteredTagErrors() ||
      this.shouldShowReviewTagErrors();
    if (shouldShowErrors) {
      const validationErrors = this.getVisibleFieldErrors(tagErrors || this.props.tagErrors);
      if (validationErrors == null) {
        return {};
      } else {
        return _.chain(validationErrors)
          .filter(({field}) => field != null) // Filter out any errors that are not field-specific.
          .groupBy(({field}) => field!.id)
          .value();
      }
    }
    return {};
  };

  private fetchValidation(props?: Props): void {
    if (props == null) {
      ({props} = this);
    }
    if (this.props.hasRegistrySupport) {
      // If the file is already registered, don't validate the file.
      if (this.isRegistered() || !TagSchemaUtil.canEditTags(props.fileId, props.permissions)) {
        return;
      }
      const schemaRegistryId = this.getSchemaRegistryId(props);
      if (schemaRegistryId != null) {
        RegistryValidatorActions.validateFileTags(this.props.fileId, schemaRegistryId);
      }
    } else if (this.shouldShowReviewTagErrors()) {
      RegistryValidatorActions.validateFileTagsForReview(this.props.fileId);
    }
  }

  private fetchSchemaValidationExtras(): void {
    if (this.isRegistered()) {
      requestFetchValidationExtras(this.props.fileId);
    }
  }

  // The only parts of results used in the register modal are the id, name, tags, tagSchema, and folderId
  private getFinderResult(): FinderResult {
    return {
      id: this.props.fileId,
      name: this.props.fileName,
      tags: this.props.tags,
      tagSchema: this.props.tagSchema,
      folder: {api_identifier: this.props.folder!.api_identifier},
    };
  }

  private isRegistrable(): boolean {
    return Boolean(
      this.props.tagSchema &&
      (!this.props.tagErrors || !this.props.tagErrors.length) &&
      this.props.hasRegistrySupport
    );
  }

  private handleRegister = (): void => {
    if (this.isRegistrable()) {
      RegistryValidatorActions.openRegistryValidatorModal([this.getFinderResult()]);
    }
  };

  private updateTags = async (
    inputData: TagUpdateOptions,
    param?: {onFailure: () => {}}
  ): Promise<Array<{id: string; tags: Array<Tag>}> | null> => {
    const {toSet, toAdd, toRemove, toRename} = inputData;
    const updateData = {
      toSet: toSet ?? {},
      toAdd: toAdd ?? [],
      toRemove: toRemove ?? [],
      toRename: toRename ?? {},
    };
    return await FinderActions.updateTagsWithDataMods([this.props.fileId], this.isRegistered(), updateData, {
      onFailure: param?.onFailure,
    });
  };

  private updateTagSchema = (tagSchema: TagSchema): void => {
    TagSchemaUtil.bulkSetTagSchema(
      [
        {
          id: this.props.fileId,
          tags: this.props.tags,
        },
      ],
      tagSchema
    );
  };

  private renderTitle(): JSX.Element {
    const isNameEditable =
      !this.props.isNonInteractiveProcess &&
      (this.props.permissions.canEditNamesAndAliases ?? this.props.isRegistryWritable) &&
      isFeatureEnabled({feature: StandardFeature.EDIT, ...this.getPartialFeatureDefinition()});

    return (
      <div
        className="metadata-header-section"
        {...getTestProps({component: 'MetadataEditorHeader', element: 'root'})}
      >
        <Tooltip
          isDisabled={this.props.permissions.canEditNamesAndAliases ?? true}
          text="You do not have permission to edit this item's name or aliases."
        >
          <div>
            <AliasField
              initialAlias={this.props.fileName}
              isFullWidth={true}
              isHeader={true}
              isWritable={isNameEditable}
              complete={this.props.updateName}
              placeholder="Name"
            />
          </div>
        </Tooltip>
        <ArchiveRecordLabel
          item={{name: this.props.fileName, id: this.props.fileId, archiveRecord: this.props.archiveRecord}}
        />
        {this.props.registryName != null && (
          <div className="metadata-registry-header">{this.props.registryName}</div>
        )}
        {this.props.hasRegistrySupport && <ConvertedToItemField fileId={this.props.fileId} />}
      </div>
    );
  }

  private renderAliasSection(): JSX.Element {
    const isAliasEditable =
      !this.props.isNonInteractiveProcess &&
      (this.props.permissions.canEditNamesAndAliases ?? this.props.isRegistryWritable) &&
      isFeatureEnabled({feature: StandardFeature.EDIT, ...this.getPartialFeatureDefinition()});

    return (
      <div className="metadata-section">
        <AliasList
          bioentityAliasInfos={this.props.bioentityAliasInfos.filter(
            (bioentityAliasInfo) =>
              bioentityAliasInfo.alias !== this.props.fileName &&
              bioentityAliasInfo.registryCollectionOwner == null
          )}
          fileId={this.props.fileId}
          isWritable={isAliasEditable}
          isRegistered={this.isRegistered()}
        />
      </div>
    );
  }

  private maybeRenderProcessMetadata(): JSX.Element | null {
    if (!APIUtil.isProcess(this.props.fileId)) {
      return null;
    }
    return (
      <div {...getTestProps({component: 'ProcessMetadataSection', element: 'root'})}>
        <ProcessMetadataSection processId={this.props.fileId} />
      </div>
    );
  }

  private maybeRenderMixtureMetadata(): JSX.Element | null {
    if (APIUtil.apiIdToType(this.props.fileId) === ModelTypeConstants.MIXTURE) {
      assert(this.props.tagSchema != null, `Expected mixture to have tag schema: ${this.props.fileId}`);
      const parentLinkMixtureTag = getParentLinkMixtureTag(this.props.tags);
      return (
        <>
          <div
            className={`${css(styles.overflowXAuto)} metadata-section`}
            {...getTestProps({component: 'IngredientTableSection', element: 'root'})}
          >
            <IngredientTable
              tagSchema={this.props.tagSchema}
              areIngredientsEditable={isMixtureEditable(this.props.permissions)}
              mixtureId={this.props.fileId}
              parentLinkFileId={parentLinkMixtureTag ? getParentLinkFileId(parentLinkMixtureTag) : null}
              isRegistered={this.isRegistered()}
            />
          </div>
          <div {...getTestProps({component: 'MixtureInstructionsSection', element: 'root'})}>
            <MixtureInstructions
              mixtureId={this.props.fileId}
              tagSchema={this.props.tagSchema}
              instructionsEditorProps={this.props.instructionsEditorProps!}
            />
          </div>
        </>
      );
    }
    return null;
  }

  private userCanEditTags(): boolean {
    return TagSchemaUtil.canEditTags(this.props.fileId, this.props.permissions);
  }

  private renderSchemaTagTable({schemaTags}: {schemaTags: Array<SchemaTag>}): JSX.Element | null {
    if (this.props.hasFileSchemaSupport) {
      const canEditSchemaTags =
        this.userCanEditTags() &&
        isFeatureEnabled({feature: StandardFeature.EDIT, ...this.getPartialFeatureDefinition()});
      return (
        <div className="metadata-section" {...getTestProps({component: 'SchemaTagTable', element: 'root'})}>
          <SchemaTagTable
            fileId={this.props.fileId}
            fileType={TagSchemaUtil.schemaTagFolderTypeForFileId(this.props.fileId)}
            hasRegistrySupport={this.props.hasRegistrySupport}
            hoistedTags={this.props.hoistedTags}
            isFileRegistered={this.isRegistered()}
            areTagsEditable={canEditSchemaTags}
            isSchemaEditable={canEditSchemaTags}
            isLoadingTags={this.props.isLoadingTags}
            sequenceSubtype={this.props.sequenceSubtype}
            onAnalyzeAaSequences={this.props.onAnalyzeAaSequences}
            projectFolderId={this.props.projectFolderId ?? null}
            schemaTags={schemaTags}
            schemaValidationState={this.props.schemaValidationState ?? null}
            sequenceParts={this.props.sequenceParts}
            errorsByFieldId={this.getFieldErrorsToDisplay()}
            tagSchema={this.props.tagSchema}
            onUpdateTags={this.updateTags}
            onUpdateTagSchema={this.updateTagSchema}
          />
        </div>
      );
    } else if (APIUtil.apiIdToType(this.props.fileId) === ModelTypeConstants.STUDY) {
      return (
        <div
          className="metadata-section"
          {...getTestProps({component: 'StudySchemaTagTableSection', element: 'root'})}
        >
          <StudySchemaTagTable studyId={this.props.fileId} />
        </div>
      );
    } else {
      return null;
    }
  }

  private maybeRenderStudyDescription(): JSX.Element | null {
    if (!APIUtil.isStudy(this.props.fileId) || !BenchlingConfig.STUDIES_ENABLE) {
      return null;
    }
    return (
      <div
        className="metadata-section"
        {...getTestProps({component: 'StudyDescriptionSection', element: 'root'})}
      >
        <div className={css(styles.descriptionStyling)}>Description</div>
        <QueryingStudyDescriptionEditor
          studyId={this.props.fileId}
          isWritable={this.props.permissions.writable}
        />
      </div>
    );
  }

  private renderCustomTagTable({
    schemaFields,
    customTags,
  }: {
    schemaFields: Array<SchemaField>;
    customTags: Array<Tag>;
  }): JSX.Element {
    return (
      <div className="metadata-section" {...getTestProps({component: 'CustomTagTable', element: 'root'})}>
        <MetadataTagTable
          canAddOrRemoveTags={isFeatureEnabled({
            feature: StandardFeature.EDIT,
            ...this.getPartialFeatureDefinition(),
          })}
          emptyMessage="This entity has no custom fields."
          fieldsForCustomTagNameValidation={schemaFields}
          fileId={this.props.fileId}
          initialTags={customTags}
          areTagsEditable={
            this.userCanEditTags() &&
            isFeatureEnabled({feature: StandardFeature.EDIT, ...this.getPartialFeatureDefinition()})
          }
          isNameEditable={isFeatureEnabled({
            feature: StandardFeature.EDIT,
            ...this.getPartialFeatureDefinition(),
          })}
          onAnalyzeAaSequences={this.props.onAnalyzeAaSequences}
          title="Custom Field"
          updateTags={this.updateTags}
          sequenceParts={this.props.sequenceParts}
        />
      </div>
    );
  }

  private maybeRenderNotificationSchedules(): JSX.Element | null {
    if (!APIUtil.isStudy(this.props.fileId) || !BenchlingConfig.STUDIES_ENABLE) {
      return null;
    }
    return (
      <div {...getTestProps({component: 'EditableNotificationsSection', element: 'root'})}>
        <EditableNotifications studyId={this.props.fileId} isWritable={this.props.permissions.writable} />
      </div>
    );
  }

  override render(): JSX.Element {
    const {schemaTags, customTags} = TagUtil.getSortedSchemaAndCustomTags({
      tags: this.state.orderedTags,
      tagSchema: this.props.tagSchema,
    });
    return (
      <div className="metadata-editor" {...getTestProps({component: 'MetadataEditor', element: 'root'})}>
        {this.props.hasFileSchemaSupport && (
          <ComputedTagFetcher
            fileId={this.props.fileId}
            aaSequenceAminoAcids={this.props.aaSequenceResidues}
            sequenceBases={this.props.sequenceBases}
            sequenceModifiedNucleotides={this.props.sequenceModifiedNucleotides}
            tags={this.props.tags}
          />
        )}
        {APIUtil.isBiologicalEntity(this.props.fileId) && (
          <ValidationStateFetcher
            aaSequenceResidues={this.props.aaSequenceResidues}
            registryId={this.props.registryId}
            sequenceBases={this.props.sequenceBases}
            sequenceCircular={this.props.sequenceCircular}
            sequenceModifiedNucleotides={this.props.sequenceModifiedNucleotides}
            // this.props.sequenceParts is not undefined because it has a defaultProp
            sequenceParts={this.props.sequenceParts!}
            // this.props.sequenceTranslations is not undefined because it has a defaultProp
            sequenceTranslations={this.props.sequenceTranslations!}
            tags={this.props.tags}
          />
        )}
        {this.props.bufferOptionsText != null && <BufferOptions banner={this.props.bufferOptionsText} />}
        <div className="react-tab-container">
          <div className="react-tab-container-narrowTab">
            <Row bufferRowReset={true}>
              {this.props.customInfoBanner != null && <Col>{this.props.customInfoBanner}</Col>}
              <Col sm={6}>{this.renderTitle()}</Col>
              <Col sm={6}>
                <MetadataEditorToolbar
                  archiveRecord={this.props.archiveRecord}
                  fileId={this.props.fileId}
                  isBioentityFailed={isFailed(this.props.schemaValidationState)}
                  fileName={this.props.fileName}
                  folder={this.props.folder}
                  hasRegistrySupport={this.props.hasRegistrySupport}
                  onRegister={this.handleRegister}
                  permissions={this.props.permissions}
                  projectFolder={this.props.projectFolder}
                  registryId={this.props.registryId ?? null}
                  schemaId={this.props.tagSchema?.id}
                  schemaRegistryId={this.getSchemaRegistryId() ?? null}
                  schemaPermissions={
                    this.props.tagSchema
                      ? {objectsRegisterable: Boolean(this.props.tagSchema.permissions.objectsRegisterable)}
                      : null
                  }
                  editURL={this.props.editURL}
                  shouldAllowChangeCollection={this.props.shouldAllowChangeCollection}
                  disableRegistering={!this.isRegistrable()}
                  isNonInteractiveProcess={this.props.isNonInteractiveProcess}
                />
              </Col>
            </Row>
            <MetadataErrors
              itemIsUnregisteredEntity={this.shouldShowUnregisteredTagErrors()}
              itemIsEntry={this.shouldShowReviewTagErrors()}
              registryId={this.props.registryId}
              schemaValidationState={this.props.schemaValidationState}
              tagErrors={this.getVisibleFieldErrors(this.props.tagErrors)}
              tagSchema={this.props.tagSchema}
              validationExtras={this.props.validationExtras}
            />
            <MetadataEditorInfoSection
              fileId={this.props.fileId}
              authors={this.props.authors}
              projectFolder={this.props.projectFolder}
              hasFileSchemaSupport={this.props.hasFileSchemaSupport}
              folder={this.props.folder}
              hasRegistrySupport={this.props.hasRegistrySupport}
              permissions={this.props.permissions}
              bioentityAliasInfos={this.props.bioentityAliasInfos}
              humanId={this.props.humanId}
              createdAt={this.props.createdAt}
              finalized={this.props.finalized}
              registryId={this.props.registryId}
              isNonInteractiveProcess={this.props.isNonInteractiveProcess}
              shouldAllowTeamAuthorsLookup={this.props.shouldAllowTeamAuthorsLookup}
            />
            {this.props.hasRegistrySupport && (
              <Row bufferRowReset={true}>
                <Col sm={12}>{this.renderAliasSection()}</Col>
              </Row>
            )}
            {this.maybeRenderMixtureMetadata()}
            {this.maybeRenderProcessMetadata()}
            {this.maybeRenderStudyDescription()}
            {this.renderSchemaTagTable({schemaTags})}
            {this.props.hasCustomTagSupport &&
              this.renderCustomTagTable({schemaFields: this.props.tagSchema?.allFields || [], customTags})}
            {this.maybeRenderNotificationSchedules()}
          </div>
        </div>
      </div>
    );
  }
}

export const ConnectedMetadataEditor = MetadataEditorConnector.connect(MetadataEditor);

const styles = StyleSheet.create({
  overflowXAuto: {
    overflowX: 'auto',
  },
  descriptionStyling: {
    marginBottom: -10,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
});
